import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { createDoctorKeywords, deleteDoctorKeywords } from '../../redux/doctorSlice';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import emptykey from '../../assets/img/colorkit (1).svg';
import { Plus, X, Trash2 } from 'lucide-react';

function KeywordsTab() {
    const { t } = useTranslation(['keywordspage']);
    const dispatch = useDispatch();
    const { selectedDoctor, loading, error } = useSelector((state) => state.doctor);
    const keywords = selectedDoctor?.advisory || [];
    const doctorId = selectedDoctor?.doctorId;

    // State for modal visibility and form inputs
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [newKeyword, setNewKeyword] = useState({ name: '', description: '' });
    const [charLimitReached, setCharLimitReached] = useState({});
    const [errors, setErrors] = useState({ name: '', description: '' });

    // Handle opening and closing modal
    const handleAddClick = () => setIsModalOpen(true);
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setNewKeyword({ name: '', description: '' }); // Reset form
        setCharLimitReached({});
        setErrors({ name: '', description: '' });
    };

    // Validate input fields
    const validateField = (name, value) => {
        let error = '';
        
        // Sanitize input - remove dangerous characters
        const sanitized = value.replace(/[<>]/g, '');
        
        if (name === 'name') {
            if (!sanitized.trim()) {
                error = t('keywordsTab.name_required');
            } else if (/^\s+$/.test(sanitized)) {
                error = t('keywordsTab.name_only_whitespace');
            } else if (sanitized.length < 2) {
                error = t('keywordsTab.name_min_2_chars');
            } else if (sanitized.length > 25) {
                error = t('keywordsTab.name_max_25_chars');
            } else if (!/^[a-zA-ZÀ-ÿ\s.,!?'-]+$/.test(sanitized)) {
                error = t('keywordsTab.name_letters_punctuation_only');
            }
        } else if (name === 'description') {
            if (!sanitized.trim()) {
                error = t('keywordsTab.description_required');
            } else if (/^\s+$/.test(sanitized)) {
                error = t('keywordsTab.description_only_whitespace');
            } else if (sanitized.length < 5) {
                error = t('keywordsTab.description_min_5_chars');
            } else if (sanitized.length > 100) {
                error = t('keywordsTab.description_max_100_chars');
            } else if (!/^[a-zA-ZÀ-ÿ\s.,!?'-]+$/.test(sanitized)) {
                error = t('keywordsTab.description_letters_punctuation_only');
            }
        }
        
        return error;
    };

    // Handle form input changes with validation
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Sanitize input - remove dangerous characters
        const sanitized = value.replace(/[<>]/g, '');

        // Disallow numbers and special characters (allow only letters, spaces, and basic punctuation)
        if (sanitized !== '' && !/^[a-zA-ZÀ-ÿ\s.,!?'-]*$/.test(sanitized)) return;

        // Enforce character limits
        if (name === 'name' && sanitized.length > 25) return;
        if (name === 'description' && sanitized.length > 100) return;

        // Update state with sanitized value
        setNewKeyword((prev) => ({ ...prev, [name]: sanitized }));
        
        // Validate and set errors
        const error = validateField(name, sanitized);
        setErrors((prev) => ({ ...prev, [name]: error }));
        
        // Check if character limit is reached
        const maxLengths = { name: 25, description: 100 };
        if (maxLengths[name] && sanitized.length >= maxLengths[name]) {
            setCharLimitReached({ ...charLimitReached, [name]: true });
        } else {
            setCharLimitReached({ ...charLimitReached, [name]: false });
        }
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate all fields
        const nameError = validateField('name', newKeyword.name);
        const descriptionError = validateField('description', newKeyword.description);
        
        const newErrors = {
            name: nameError,
            description: descriptionError
        };
        
        setErrors(newErrors);

        // Check if there are any errors
        if (nameError || descriptionError) {
            return;
        }

        if (!(newKeyword.name.trim() && newKeyword.description.trim())) {
            toast.warn(t('keywordspage.error_missing_fields'));
            return;
        }

        const result = await Swal.fire({
            title: t('keywordspage.confirm_create_title'),
            text: t('keywordspage.confirm_create_text', { name: newKeyword.name }),
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: t('keywordspage.confirm_create_yes'),
            cancelButtonText: t('keywordspage.cancel'),
            confirmButtonColor: "#4ABDBD",
            cancelButtonColor: "#d33",
        });

        if (!result.isConfirmed) return;

        try {
            const payload = {
                name: newKeyword.name.trim(),
                description: newKeyword.description.trim(),
            };

            await dispatch(
                createDoctorKeywords({ payload, doctorId })
            ).unwrap();

            handleCloseModal();

            toast.success(t('keywordspage.create_success_text', { name: newKeyword.name }));
        } catch (err) {
            console.error("Failed to create keyword:", err);
            toast.warn(err?.message || t('keywordspage.error_general'));
        }
    };

    // Handle keyword deletion with SweetAlert confirmation
    const handleDeleteKeyword = async (keywordId) => {
        const result = await Swal.fire({
            title: t('keywordspage.delete_confirm_title'),
            text: t('keywordspage.delete_confirm_text'),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: "#4ABDBD",
            confirmButtonText: t('keywordspage.delete_confirm_button'),
            cancelButtonText: t('keywordspage.cancel'),
        });

        if (!result.isConfirmed) return;

        try {
            await dispatch(deleteDoctorKeywords({ doctorId,keywordId })).unwrap();

            toast.success(t('keywordspage.delete_success_text'));
        } catch (err) {
            toast.warn(err?.message || t('keywordspage.error_general'));
        }
    };

    return (
        <div data-testid="keywords-tab-container" className="mt-4">
            {/* Header Row */}
            <div data-testid="keywords-tab-header" className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <h2 data-testid="keywords-tab-title" className="text-lg sm:text-xl md:text-2xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                    {t('keywordspage.heading')}
                </h2>
                <button
                    data-testid="add-keyword-button"
                    onClick={handleAddClick}
                    disabled={loading}
                    className={`group flex items-center gap-2 w-full sm:w-auto cursor-pointer px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold relative overflow-hidden text-sm ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                    <Plus data-testid="add-keyword-icon" className="w-5 h-5" />
                    <span data-testid="add-keyword-text">{t('keywordspage.add_keyword')}</span>
                </button>
            </div>

            {/* Table for larger screens, Cards for mobile */}
            <div className={`hidden sm:block overflow-x-auto rounded-md  ${keywords.length === 0 ? '' : 'shadow'}`}>
                <div className="max-h-[340px] custom-scrollbargray">
                    {keywords.length === 0 ? (
                        <div data-testid="no-keywords-empty-state" className="flex flex-col items-center justify-center col-span-full min-h-[200px] px-4 sm:px-6 lg:px-8">
                            <img
                                data-testid="no-keywords-image"
                                src={emptykey}
                                alt={t('keywordsTab.no_keyword_found')}
                                className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 lg:w-56 lg:h-56 object-contain"
                            />
                            <p data-testid="no-keywords-text" className="text-gray-500 text-center text-sm sm:text-base md:text-lg">
                                {t('keywordspage.empty')}
                            </p>
                        </div>
                    ) : (
                        <table data-testid="keywords-table" className="w-full border-collapse rounded-md overflow-hidden">
                            <thead>
                                <tr data-testid="table-header" className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                    <th data-testid="name-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('keywordspage.name')}</th>
                                    <th data-testid="description-header" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('keywordspage.description')}</th>
                                    <th data-testid="action-header" className="p-3 text-center text-xs font-bold uppercase tracking-wider">{t('keywordspage.action')}</th>
                                </tr>
                            </thead>
                            <tbody data-testid="keywords-table-body">
                                {keywords.map((keyword, index) => (
                                    <tr key={keyword.id} data-testid={`keyword-row-${keyword.id}`} className="border-b border-gray-100/50 hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80 transition-all duration-300 group">
                                        <td data-testid={`keyword-name-${keyword.id}`} className="p-3 text-sm font-semibold text-gray-800 group-hover:text-gray-900 transition-colors duration-300">{keyword.name}</td>
                                        <td data-testid={`keyword-description-${keyword.id}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700 transition-colors duration-300">{keyword.description}</td>
                                        <td data-testid={`keyword-actions-${keyword.id}`} className="p-3 text-xs flex justify-center gap-2">
                                            <button
                                                data-testid={`delete-keyword-${keyword.id}`}
                                                onClick={() => handleDeleteKeyword(keyword.advisoryId)}
                                                className="group/btn text-red-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-500 hover:to-pink-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                disabled={loading}
                                            >
                                                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                <Trash2 data-testid={`delete-icon-${keyword.id}`} className="h-4 w-4 relative z-10" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Card layout for mobile screens */}
            <div className="block sm:hidden space-y-4">
                {keywords.length > 0 ? (
                    keywords.map((keyword, index) => (
                        <div
                            key={keyword.id}
                            data-testid={`keyword-card-${keyword.id}`}
                            className="p-4 bg-gradient-to-r bg-gray-50 rounded-xl shadow-md hover:shadow-lg transition-all duration-300"
                        >
                            <div className="flex justify-between items-center mb-2">
                                <span data-testid={`keyword-serial-${keyword.id}`} className="text-xs font-bold text-[#4ABDBD]">
                                    {t('keywordspage.serial_no')}: {index + 1}
                                </span>
                                <div className="flex gap-2">
                                    <button
                                        data-testid={`delete-keyword-${keyword.id}`}
                                        onClick={() => handleDeleteKeyword(keyword.advisoryId)}
                                        className="group/btn text-red-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-500 hover:to-pink-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                        disabled={loading}
                                    >
                                        <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                        <Trash2 data-testid={`delete-icon-${keyword.id}`} className="h-4 w-4 relative z-10" />
                                    </button>
                                </div>
                            </div>
                            <div className="space-y-2">
                                <div data-testid={`keyword-name-${keyword.id}`} className="text-sm font-semibold text-gray-800">
                                    <span className="font-bold">{t('keywordspage.name')}:</span> {keyword.name}
                                </div>
                                <div data-testid={`keyword-description-${keyword.id}`} className="text-xs text-gray-600">
                                    <span className="font-bold">{t('keywordspage.description')}:</span> {keyword.description}
                                </div>
                            </div>
                        </div>
                    ))
                ) : (
                    <div data-testid="no-keywords-row" className="p-3 text-center text-gray-500">
                        {t('keywordspage.empty')}
                    </div>
                )}
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div
                    className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
                    role="dialog"
                    aria-labelledby="keyword-modal-title"
                    id="keyword-modal"
                    data-testid="modal"
                >
                    <div className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-md sm:max-w-lg md:max-w-2xl max-h-[80vh] sm:max-h-[90vh] flex flex-col">
                        {/* Header Section - Sticky */}
                        <div className="p-4 sm:p-6 sticky top-0 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200 rounded-t-2xl">
                            <h2
                                className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent"
                                id="keyword-modal-title"
                                data-testid="modal-title"
                            >
                                {t('keywordspage.modal_title')}
                            </h2>
                            <button
                                id="keyword-modal-close-button"
                                onClick={handleCloseModal}
                                aria-label={t('keywordspage.cancel')}
                                className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                disabled={loading}
                                data-testid="modal-close-button"
                            >
                                <X className="w-5 h-5 sm:w-6 sm:h-6" />
                            </button>
                        </div>

                        {/* Scrollable Form Content */}
                        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar">
                            <form id="keyword-form" onSubmit={handleSubmit} data-testid="keyword-form" className="space-y-4">
                                <div className="mb-4">
                                    <label
                                        className="block text-sm font-semibold text-gray-700 mb-2"
                                        htmlFor="name"
                                        id="name-label"
                                        data-testid="name-label"
                                    >
                                        {t('keywordspage.label_name')} <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={newKeyword.name}
                                        onChange={handleInputChange}
                                        className={`w-full p-3 rounded-xl border text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${
                                            errors.name 
                                                ? 'border-red-500 bg-red-50' 
                                                : 'border-gray-300 hover:border-[#4ABDBD]'
                                        }`}
                                        required
                                        disabled={loading}
                                        maxLength={25}
                                        data-testid="keyword-name-input"
                                    />
                                    {errors.name && (
                                        <p data-testid="keyword-name-error" className="mt-1 text-xs text-red-500 font-medium">{errors.name}</p>
                                    )}
                                    {charLimitReached.name && !errors.name && (
                                        <p data-testid="keyword-name-char-limit" className="mt-1 text-xs text-red-500 font-medium">{t('keywordspage.reached_text_limit', { limit: 25 })}</p>
                                    )}
                                </div>
                                <div className="mb-4">
                                    <label
                                        className="block text-sm font-semibold text-gray-700 mb-2"
                                        htmlFor="description"
                                        id="description-label"
                                        data-testid="description-label"
                                    >
                                        {t('keywordspage.label_description')} <span className="text-red-500">*</span>
                                    </label>
                                    <textarea
                                        id="description"
                                        name="description"
                                        value={newKeyword.description}
                                        onChange={handleInputChange}
                                        className={`w-full p-3 rounded-xl border text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all duration-300 ${
                                            errors.description 
                                                ? 'border-red-500 bg-red-50' 
                                                : 'border-gray-300 hover:border-[#4ABDBD]'
                                        }`}
                                        rows="4"
                                        required
                                        disabled={loading}
                                        maxLength={100}
                                        data-testid="keyword-description-input"
                                    />
                                    {errors.description && (
                                        <p data-testid="keyword-description-error" className="mt-1 text-xs text-red-500 font-medium">{errors.description}</p>
                                    )}
                                    {charLimitReached.description && !errors.description && (
                                        <p data-testid="keyword-description-char-limit" className="mt-1 text-xs text-red-500 font-medium">{t('keywordspage.reached_text_limit', { limit: 100 })}</p>
                                    )}
                                    {/* <p className="text-xs text-gray-500 mt-1">
                                        {newKeyword.description.length}/100 {t('keywordspage.characters')}
                                    </p> */}
                                </div>
                            </form>
                        </div>

                        {/* Footer Section - Sticky */}
                        <div className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-gradient-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-4">
                            <button
                                type="button"
                                id="keyword-form-cancel-button"
                                onClick={handleCloseModal}
                                className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer"
                                disabled={loading}
                                data-testid="cancel-button"
                            >
                                {t('keywordspage.cancel')}
                            </button>
                            <button
                                type="submit"
                                id="keyword-form-submit-button"
                                form="keyword-form"
                                className={`group flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm ${loading ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 cursor-pointer'}`}
                                disabled={loading}
                                data-testid="create-button"
                            >
                                {loading && (
                                    <span data-testid="loading-spinner" className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                                )}
                                {loading ? t('keywordspage.loading') : t('keywordspage.create')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default KeywordsTab;